const DEALER_FINDER_STATE_NAMES = {
  AZ: 'Arizona',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  GA: 'Georgia',
  IL: 'Illinois',
  MA: 'Massachusetts',
  MD: 'Maryland',
  MI: 'Michigan',
  NJ: 'New Jersey',
  NV: 'Nevada',
  NY: 'New York',
  OR: 'Oregon',
  RI: 'Rhode Island',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VA: 'Virginia',
  WA: 'Washington'
};

const DEALER_FINDER_MAP_COLORS = {
  ink: '#142c4b',
  accent: '#ffddbf',
  cream: '#fff7ef',
  white: '#ffffff'
};

if (!customElements.get('dealer-finder')) {
  class DealerFinder extends HTMLElement {
    constructor() {
      super();

      this.defaultCenter = { lat: 39.8283, lng: -98.5795 };
      this.map = null;
      this.markers = [];
      this.markerLookup = new Map();
      this.origin = null;

      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleInput = this.handleInput.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.form = this.querySelector('[data-dealer-form]');
      this.searchInput = this.querySelector('[data-dealer-search]');
      this.stateSelect = this.querySelector('[data-dealer-state]');
      this.radiusSelect = this.querySelector('[data-dealer-radius]');
      this.summary = this.querySelector('[data-dealer-summary]');
      this.feedback = this.querySelector('[data-dealer-feedback]');
      this.empty = this.querySelector('[data-dealer-empty]');
      this.cardsContainer = this.querySelector('[data-dealer-results]');
      this.cardTemplate = this.querySelector('[data-dealer-card-template]');
      this.mapRoot = this.querySelector('[data-dealer-map]');
      this.mapNote = this.querySelector('[data-dealer-map-note]');
      this.showMap = this.dataset.showMap === 'true' && !!this.mapRoot;
      this.sourceUrl = this.dataset.sourceUrl || '';
      this.dealers = [];
      this.dataLoadError = false;

      if (this.radiusSelect && this.dataset.defaultRadius) {
        this.radiusSelect.value = this.dataset.defaultRadius;
      }

      this.bindEvents();
      this.initialize();
    }

    async initialize() {
      if (this.sourceUrl) {
        await this.loadDealersFromSource();
      } else {
        this.refreshDealers();
      }

      this.populateStateOptions();
      this.updateResults();
    }

    bindEvents() {
      this.form?.addEventListener('submit', this.handleSubmit);
      this.searchInput?.addEventListener('input', this.handleInput);
      this.stateSelect?.addEventListener('change', this.handleChange);
      this.radiusSelect?.addEventListener('change', this.handleChange);
      this.addEventListener('click', this.handleClick);

      if (window.Shopify && Shopify.designMode) {
        this.addEventListener('shopify:block:select', this.handleBlockSelect);
      }
    }

    parseDealer(element, index) {
      return {
        id: element.dataset.dealerId || `dealer-${index}`,
        index,
        element,
        name: element.dataset.name || '',
        city: element.dataset.city || '',
        state: element.dataset.state || '',
        zip: element.dataset.zip || '',
        address: element.dataset.address || '',
        search: element.dataset.search || '',
        lat: this.parseNumber(element.dataset.lat),
        lng: this.parseNumber(element.dataset.lng)
      };
    }

    refreshDealers() {
      this.dealers = Array.from(this.querySelectorAll('[data-dealer-card]')).map((element, index) => this.parseDealer(element, index));
    }

    populateStateOptions() {
      if (!this.stateSelect) return;

      while (this.stateSelect.options.length > 1) {
        this.stateSelect.remove(1);
      }

      const states = [...new Set(this.dealers.map((dealer) => dealer.state.trim()).filter(Boolean))].sort((left, right) => left.localeCompare(right));
      const currentValue = this.stateSelect.value;

      states.forEach((state) => {
        const option = document.createElement('option');
        option.value = state;
        option.textContent = state;
        this.stateSelect.appendChild(option);
      });

      this.stateSelect.value = currentValue;
    }

    async loadDealersFromSource() {
      if (!this.sourceUrl || !this.cardsContainer || !this.cardTemplate) {
        this.refreshDealers();
        return;
      }

      if (this.summary) {
        this.summary.innerHTML = '<strong>Loading dealers...</strong>';
      }

      try {
        const response = await fetch(this.sourceUrl, {
          credentials: 'same-origin'
        });

        if (!response.ok) {
          throw new Error(`HTTP_${response.status}`);
        }

        const payload = await response.json();
        const records = Array.isArray(payload) ? payload : Array.isArray(payload?.dealers) ? payload.dealers : [];

        this.renderDealerCards(records);
        this.refreshDealers();
      } catch (error) {
        this.dataLoadError = true;
        this.cardsContainer.innerHTML = '';
        this.refreshDealers();
        this.setFeedback('The dealer directory could not be loaded right now. Check the JSON asset and try again.');

        if (this.summary) {
          this.summary.innerHTML = '<strong>Dealer directory unavailable.</strong>';
        }

        if (this.empty) {
          this.empty.hidden = false;
        }
      }
    }

    handleInput() {
      this.updateResults();
    }

    handleChange() {
      this.updateResults();
    }

    async handleSubmit(event) {
      event.preventDefault();

      const query = this.searchInput?.value.trim() || '';

      if (!query) {
        this.setFeedback('');
        this.updateResults();
        return;
      }

      this.setFeedback('');
      this.updateResults();
    }

    handleClick(event) {
      const action = event.target.closest('[data-action]');
      if (action) {
        event.preventDefault();

        switch (action.dataset.action) {
          case 'geolocate':
            this.handleGeolocate();
            return;
          case 'reset':
            this.resetFilters();
            return;
          default:
            break;
        }
      }

      const focusMap = event.target.closest('[data-focus-map]');
      if (focusMap) {
        const card = focusMap.closest('[data-dealer-card]');
        if (card) {
          this.focusDealerById(card.dataset.dealerId, true);
        }
      }
    }

    handleBlockSelect(event) {
      const card = event.target.closest('[data-dealer-card]');
      if (!card) return;

      this.resetFilters();
      window.requestAnimationFrame(() => this.focusDealerById(card.dataset.dealerId, true));
    }

    async handleGeolocate() {
      if (!navigator.geolocation) {
        this.setFeedback('Your browser does not support location access. Try searching with a ZIP code or city.');
        return;
      }

      this.setBusy(true);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.origin = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            label: 'your location',
            query: '',
            source: 'device'
          };
          if (this.searchInput) {
            this.searchInput.value = '';
          }
          this.setFeedback('');
          this.setBusy(false);
          this.updateResults();
        },
        () => {
          this.setBusy(false);
          this.setFeedback('Location access was blocked. Try a ZIP code, city, or state instead.');
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000
        }
      );
    }

    resetFilters() {
      this.origin = null;
      this.setFeedback('');

      if (this.searchInput) this.searchInput.value = '';
      if (this.stateSelect) this.stateSelect.value = '';
      if (this.radiusSelect) this.radiusSelect.value = this.dataset.defaultRadius || '';

      this.updateResults();
    }

    updateResults() {
      if (this.dataLoadError) {
        return;
      }

      const query = this.normalize(this.searchInput?.value || '');
      const state = this.normalize(this.stateSelect?.value || '');
      const radius = this.radiusSelect?.value ? parseFloat(this.radiusSelect.value) : null;

      let results = this.dealers.map((dealer) => {
        const distance = this.origin && Number.isFinite(dealer.lat) && Number.isFinite(dealer.lng)
          ? this.calculateMiles(this.origin.lat, this.origin.lng, dealer.lat, dealer.lng)
          : null;

        return {
          ...dealer,
          distance,
          score: query ? this.matchScore(dealer, query) : 0
        };
      });

      if (state) {
        results = results.filter((dealer) => this.normalize(dealer.state) === state);
      }

      if (this.origin) {
        results = results.filter((dealer) => Number.isFinite(dealer.distance));

        if (radius) {
          results = results.filter((dealer) => dealer.distance <= radius);
        }

        results.sort((left, right) => {
          if ((left.distance || 0) !== (right.distance || 0)) {
            return (left.distance || 0) - (right.distance || 0);
          }
          return left.name.localeCompare(right.name);
        });
      } else if (query) {
        results = results.filter((dealer) => dealer.score > 0);
        results.sort((left, right) => {
          if (left.score !== right.score) {
            return right.score - left.score;
          }
          return left.name.localeCompare(right.name);
        });
      } else {
        results.sort((left, right) => {
          const stateCompare = left.state.localeCompare(right.state);
          if (stateCompare !== 0) return stateCompare;

          const cityCompare = left.city.localeCompare(right.city);
          if (cityCompare !== 0) return cityCompare;

          return left.name.localeCompare(right.name);
        });
      }

      this.renderResults(results);
      this.updateSummary(results, query, state, radius);
      this.updateMap(results);
    }

    renderDealerCards(records) {
      if (!this.cardsContainer || !this.cardTemplate) return;

      const fragment = document.createDocumentFragment();

      records.forEach((record, index) => {
        const normalized = this.normalizeDealerRecord(record, index);
        if (!normalized.name && !normalized.street) return;
        fragment.appendChild(this.buildDealerCard(normalized));
      });

      this.cardsContainer.replaceChildren(fragment);
    }

    renderResults(results) {
      const visibleIds = new Set(results.map((dealer) => dealer.id));

      this.dealers.forEach((dealer) => {
        const distanceBadge = dealer.element.querySelector('[data-dealer-distance]');
        const result = results.find((item) => item.id === dealer.id);

        if (!result) {
          dealer.element.hidden = true;
          dealer.element.classList.remove('is-active');
          dealer.element.style.order = '';
          if (distanceBadge) {
            distanceBadge.hidden = true;
            distanceBadge.textContent = '';
          }
          return;
        }

        dealer.element.hidden = false;
        dealer.element.style.order = results.indexOf(result);

        if (distanceBadge) {
          if (Number.isFinite(result.distance)) {
            distanceBadge.hidden = false;
            distanceBadge.textContent = `${this.formatMiles(result.distance)} away`;
          } else {
            distanceBadge.hidden = true;
            distanceBadge.textContent = '';
          }
        }
      });

      if (this.empty) {
        this.empty.hidden = visibleIds.size > 0;
      }
    }

    updateSummary(results, query, state, radius) {
      if (!this.summary) return;

      const count = results.length;
      const dealersWord = count === 1 ? 'dealer' : 'dealers';

      if (count === 0) {
        this.summary.innerHTML = '<strong>0 dealers</strong> matched the current search.';
        return;
      }

      if (this.origin) {
        if (radius) {
          this.summary.innerHTML = `<strong>${count} ${dealersWord}</strong> within ${radius} miles of ${this.escapeHtml(this.origin.label)}.`;
        } else {
          this.summary.innerHTML = `<strong>${count} ${dealersWord}</strong> sorted by distance from ${this.escapeHtml(this.origin.label)}.`;
        }
        return;
      }

      if (query) {
        this.summary.innerHTML = `<strong>${count} ${dealersWord}</strong> matched “${this.escapeHtml(this.searchInput.value.trim())}”.`;
        return;
      }

      if (state) {
        this.summary.innerHTML = `<strong>${count} ${dealersWord}</strong> in ${this.escapeHtml(this.stateSelect.value)}.`;
        return;
      }

      this.summary.innerHTML = `<strong>${count} ${dealersWord}</strong> across the USA.`;
    }

    normalizeDealerRecord(record, index) {
      const source = record && typeof record === 'object' ? record : {};

      return {
        id: String(source.id || source.handle || `dealer-${index + 1}`),
        name: this.cleanText(source.name),
        street: this.cleanText(source.street),
        address2: this.cleanText(source.address_2 || source.address2),
        city: this.cleanText(source.city),
        state: this.cleanText(source.state),
        zip: this.cleanText(source.zip),
        phone: this.cleanText(source.phone),
        email: this.cleanText(source.email),
        websiteUrl: this.normalizeWebsiteUrl(source.website_url || source.websiteUrl),
        websiteLabel: this.cleanText(source.website_label || source.websiteLabel) || 'Website',
        latitude: this.parseNumber(source.latitude ?? source.lat),
        longitude: this.parseNumber(source.longitude ?? source.lng)
      };
    }

    buildDealerCard(record) {
      const element = this.cardTemplate.content.firstElementChild.cloneNode(true);
      const fullAddress = this.buildFullAddress(record);
      const addressMarkup = this.buildAddressMarkup(record);
      const search = this.buildSearchBlob(record, fullAddress);

      element.dataset.dealerId = record.id;
      element.dataset.name = record.name;
      element.dataset.city = record.city;
      element.dataset.state = record.state;
      element.dataset.zip = record.zip;
      element.dataset.address = fullAddress;
      element.dataset.search = search.toLowerCase();
      element.dataset.lat = Number.isFinite(record.latitude) ? String(record.latitude) : '';
      element.dataset.lng = Number.isFinite(record.longitude) ? String(record.longitude) : '';

      const name = element.querySelector('[data-dealer-name]');
      if (name) name.textContent = record.name;

      const locality = element.querySelector('[data-dealer-locality]');
      if (locality) {
        const localityText = [record.city, record.state].filter(Boolean).join(', ');
        locality.hidden = localityText === '';
        locality.textContent = localityText;
      }

      const addressDisplay = element.querySelector('[data-address-display]');
      if (addressDisplay) {
        addressDisplay.innerHTML = addressMarkup;
      }

      const phoneLink = element.querySelector('[data-phone-link]');
      if (phoneLink) {
        const tel = this.normalizePhoneLink(record.phone);
        phoneLink.hidden = !record.phone || !tel;
        if (tel) phoneLink.href = tel;
        const phoneText = phoneLink.querySelector('[data-phone-text]');
        if (phoneText) phoneText.textContent = record.phone;
      }

      const emailLink = element.querySelector('[data-email-link]');
      if (emailLink) {
        emailLink.hidden = !record.email;
        if (record.email) emailLink.href = `mailto:${record.email}`;
        const emailText = emailLink.querySelector('[data-email-text]');
        if (emailText) emailText.textContent = record.email;
      }

      const directionsLink = element.querySelector('[data-directions-link]');
      if (directionsLink) {
        directionsLink.href = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
      }

      const websiteLink = element.querySelector('[data-website-link]');
      if (websiteLink) {
        websiteLink.hidden = !record.websiteUrl;
        if (record.websiteUrl) websiteLink.href = record.websiteUrl;
        const websiteLabel = websiteLink.querySelector('[data-website-label]');
        if (websiteLabel) websiteLabel.textContent = record.websiteLabel;
      }

      return element;
    }

    updateMap(results) {
      if (!this.showMap) return;

      const map = this.ensureMap();
      if (!map) return;

      this.clearMarkers();

      const bounds = [];

      if (this.origin) {
        const originMarker = this.createMarker([this.origin.lat, this.origin.lng], {
          origin: true,
          title: this.origin.label
        });

        originMarker.addTo(map);
        originMarker.bindPopup(
          `<div class="dealer-finder__map-info"><h4>Your location</h4><p>${this.escapeHtml(this.origin.label)}</p></div>`,
          {
            closeButton: false,
            offset: [0, -8]
          }
        );

        this.markers.push(originMarker);
        bounds.push([this.origin.lat, this.origin.lng]);
      }

      results.forEach((dealer) => {
        if (!Number.isFinite(dealer.lat) || !Number.isFinite(dealer.lng)) return;

        const marker = this.createMarker([dealer.lat, dealer.lng], {
          title: dealer.name
        });

        marker.addTo(map);
        const popupContent = this.buildInfoWindowContent(dealer.element);
        if (popupContent) {
          marker.bindPopup(popupContent, {
            closeButton: false,
            offset: [0, -8]
          });
        }

        marker.on('click', () => this.focusDealerById(dealer.id, true, marker));
        this.markers.push(marker);
        this.markerLookup.set(dealer.id, marker);
        bounds.push([dealer.lat, dealer.lng]);
      });

      if (bounds.length === 0) {
        map.setView([this.defaultCenter.lat, this.defaultCenter.lng], parseInt(this.dataset.mapZoom || '5', 10));
        return;
      }

      if (bounds.length === 1) {
        map.setView(bounds[0], Math.max(parseInt(this.dataset.mapZoom || '5', 10), 8));
        return;
      }

      map.fitBounds(bounds, {
        padding: [40, 40],
        maxZoom: 10
      });

      window.requestAnimationFrame(() => map.invalidateSize());
    }

    ensureMap() {
      if (this.map) return this.map;
      if (!this.mapRoot) return null;

      if (!window.L?.map) {
        this.toggleMapFallback(true);
        this.setFeedback('The interactive map could not be loaded right now, but the dealer search still works.');
        return null;
      }

      this.toggleMapFallback(false);

      this.map = window.L.map(this.mapRoot, {
        center: [this.defaultCenter.lat, this.defaultCenter.lng],
        zoom: parseInt(this.dataset.mapZoom || '5', 10),
        scrollWheelZoom: false,
        zoomControl: true,
        attributionControl: true
      });

      window.L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors'
      }).addTo(this.map);

      window.requestAnimationFrame(() => this.map.invalidateSize());

      return this.map;
    }

    clearMarkers() {
      this.markers.forEach((marker) => marker.remove());
      this.markers = [];
      this.markerLookup.clear();
    }

    focusDealerById(id, scrollIntoView = false, markerOverride = null) {
      const dealer = this.dealers.find((item) => item.id === id);
      if (!dealer) return;

      this.dealers.forEach((item) => item.element.classList.toggle('is-active', item.id === id));

      if (scrollIntoView) {
        dealer.element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      if (!this.map) return;

      const marker = markerOverride || this.markerLookup.get(id);
      if (!marker) return;

      this.map.panTo(marker.getLatLng(), {
        animate: true,
        duration: 0.35
      });
      marker.openPopup();
    }

    createMarker(coordinates, options = {}) {
      const markerOptions = options.origin
        ? {
            radius: 8,
            color: DEALER_FINDER_MAP_COLORS.white,
            weight: 3,
            fillColor: DEALER_FINDER_MAP_COLORS.ink,
            fillOpacity: 1
          }
        : {
            radius: 7,
            color: DEALER_FINDER_MAP_COLORS.ink,
            weight: 2,
            fillColor: DEALER_FINDER_MAP_COLORS.cream,
            fillOpacity: 1
          };

      return window.L.circleMarker(coordinates, markerOptions);
    }

    toggleMapFallback(isVisible) {
      if (this.mapRoot) {
        this.mapRoot.hidden = Boolean(isVisible);
      }

      if (this.mapNote) {
        this.mapNote.hidden = !isVisible;
      }
    }

    matchScore(dealer, query) {
      if (!query) return 0;

      const name = this.normalize(dealer.name);
      const city = this.normalize(dealer.city);
      const stateTerms = this.getStateSearchTerms(dealer.state).map((term) => this.normalize(term));
      const zip = this.normalize(dealer.zip);
      const search = this.normalize(dealer.search);

      if (zip === query) return 120;
      if (city === query) return 110;
      if (stateTerms.includes(query)) return 100;
      if (name === query) return 90;
      if (zip.startsWith(query)) return 80;
      if (city.includes(query)) return 70;
      if (stateTerms.some((term) => term.startsWith(query))) return 65;
      if (stateTerms.some((term) => term.includes(query))) return 60;
      if (name.includes(query)) return 50;
      if (search.includes(query)) return 25;

      return 0;
    }

    calculateMiles(lat1, lng1, lat2, lng2) {
      const toRadians = (value) => value * (Math.PI / 180);
      const earthRadiusMiles = 3958.7613;
      const dLat = toRadians(lat2 - lat1);
      const dLng = toRadians(lng2 - lng1);
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRadians(lat1)) *
        Math.cos(toRadians(lat2)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);

      return earthRadiusMiles * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    parseNumber(value) {
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : null;
    }

    cleanText(value) {
      return (value || '').toString().trim();
    }

    normalizeWebsiteUrl(value) {
      const cleaned = this.cleanText(value);
      if (!cleaned) return '';
      if (/^https?:\/\//i.test(cleaned)) return cleaned;
      return `https://${cleaned}`;
    }

    normalizePhoneLink(value) {
      const digits = this.cleanText(value).replace(/[^+\d]/g, '');
      return digits ? `tel:${digits}` : '';
    }

    buildSearchBlob(record, fullAddress) {
      return [
        record.name,
        fullAddress,
        record.city,
        record.state,
        ...this.getStateSearchTerms(record.state),
        record.zip,
        record.phone,
        record.email
      ].filter(Boolean).join(' ');
    }

    getStateSearchTerms(state) {
      const abbreviation = this.cleanText(state).toUpperCase();
      const fullName = DEALER_FINDER_STATE_NAMES[abbreviation] || '';

      return [abbreviation, fullName].filter(Boolean);
    }

    buildInfoWindowContent(element) {
      if (!element) return '';

      const name = this.cleanText(element.querySelector('[data-dealer-name]')?.textContent);
      const addressMarkup = element.querySelector('[data-address-display]')?.innerHTML?.trim() || '';
      const phone = this.cleanText(element.querySelector('[data-phone-text]')?.textContent);
      const email = this.cleanText(element.querySelector('[data-email-text]')?.textContent);

      const parts = [];

      if (name) {
        parts.push(`<h4>${this.escapeHtml(name)}</h4>`);
      }

      if (addressMarkup) {
        parts.push(`<address>${addressMarkup}</address>`);
      }

      if (phone) {
        parts.push(`<p>${this.escapeHtml(phone)}</p>`);
      }

      if (email) {
        parts.push(`<p>${this.escapeHtml(email)}</p>`);
      }

      if (parts.length === 0) return '';

      return `<div class="dealer-finder__map-info">${parts.join('')}</div>`;
    }

    buildFullAddress(record) {
      const lineOne = [record.street, record.address2].filter(Boolean).join(', ');
      const lineTwo = [record.city, record.state].filter(Boolean).join(', ');
      const zipPart = record.zip ? `${lineTwo ? ' ' : ''}${record.zip}` : '';
      return [lineOne, `${lineTwo}${zipPart}`.trim(), 'USA'].filter(Boolean).join(', ');
    }

    buildAddressMarkup(record) {
      const lines = [];

      if (record.street) lines.push(this.escapeHtml(record.street));
      if (record.address2) lines.push(this.escapeHtml(record.address2));

      const localityParts = [record.city, record.state].filter(Boolean).map((part) => this.escapeHtml(part));
      const locality = localityParts.join(', ');
      const lastLine = `${locality}${record.zip ? `${locality ? ' ' : ''}${this.escapeHtml(record.zip)}` : ''}`.trim();
      if (lastLine) lines.push(lastLine);

      return lines.join('<br>');
    }

    normalize(value) {
      return (value || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
    }

    formatMiles(value) {
      if (!Number.isFinite(value)) return '';
      return value < 10 ? `${value.toFixed(1)} mi` : `${Math.round(value)} mi`;
    }

    setBusy(isBusy) {
      this.toggleAttribute('aria-busy', Boolean(isBusy));

      this.querySelectorAll('[data-action], button[type="submit"]').forEach((button) => {
        if ('disabled' in button) {
          button.disabled = Boolean(isBusy);
        }
      });
    }

    setFeedback(message) {
      if (!this.feedback) return;

      if (!message) {
        this.feedback.hidden = true;
        this.feedback.textContent = '';
        return;
      }

      this.feedback.hidden = false;
      this.feedback.textContent = message;
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  customElements.define('dealer-finder', DealerFinder);
}
